/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import SocialShare from "@qlik/qlik/src/components/Common/SocialShare";
import * as styles from "@qlik/qlik/src/components/RichText/styles.module.css";
import RichText from "@qlik/qlik/src/components/RichText";
import { useDebouncedCallback } from "@qlik/qlik/src/hooks/useDebouncedCallback";
import { fetchBlogListing } from "@qlik/qlik/src/lib/services/blog/api";
import {
    BLOG_TERM_TOPICS,
    BLOG_TERM_TRENDS,
    BLOG_TERM_INDUSTRIES,
    BLOG_TERM_AUTHOR,
    BLOG_PATHNAME,
    BLOG_VALIDATION_NO_RESULTS,
    BLOG_CATEGORIES_HEADING,
    BLOG_RELATED_POSTS_HEADING,
} from "@qlik/qlik/src/lib/constants/blog-constants";
import { generateRelatedPostsData, getMicrocopyValueFromKeys } from "@qlik/qlik/src/lib/util/helpers";
import Link from "@qlik/qlik/src/components/Link";
import CardImage from "@qlik/qlik/src/components/CardDeckContainer/Card_Image";
import { getTransformedId } from "@qlik/qlik/src/lib/util/format";
import { blogMapper } from "@qlik/qlik/src/lib/util/mappers/contentful-response-mappers";
import * as blogStyles from "./styles.module.css";

/**
 * Template component for Blog inner page.
 * It displays the content type and rendering of the passed data.P
 *
 * @param {Object} data - The data to be displayed.
 * @returns {JSX.Element} A section element containing the content type and rendering of the data.
 */
const BlogSingleEntry = ({ data }) => {
    const { title, slug, industry, topic, trend } = data || {};
    const [relatedPosts, setRelatedPosts] = useState();
    const [filters, setFilters] = useState({
        page: 0,
        limit: 4,
        query: "",
        [BLOG_TERM_TOPICS]: [],
        [BLOG_TERM_TRENDS]: [],
        [BLOG_TERM_INDUSTRIES]: [],
        [BLOG_TERM_AUTHOR]: [],
    });

    const [
        noResultsValidation,
        blogCategoriesHeading,
        relatedPostsHeading,
    ] = getMicrocopyValueFromKeys(data?.microcopyGroup, [
        BLOG_VALIDATION_NO_RESULTS,
        BLOG_CATEGORIES_HEADING,
        BLOG_RELATED_POSTS_HEADING,
    ]);

    // fetch resource entries as per the filter
    const fetchBlogs = useDebouncedCallback(async () => {
        let isMounted = true;
        try {
            const { results: { items = [] } = {} } = await fetchBlogListing(filters);
            if (!items) throw new Error(noResultsValidation);
            const relatedPostsData = generateRelatedPostsData(blogMapper(items), title, relatedPostsHeading);
            if (isMounted) {
                // Check if the component is still mounted
                setRelatedPosts(relatedPostsData);
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error);
        }
        return () => {
            isMounted = false;
        };
    });

    useEffect(() => {
        const newFilters = { ...filters };

        if (!industry && !topic && !trend) {
            return;
        }

        if (topic && topic.length > 0) {
            newFilters[BLOG_TERM_TOPICS] = [topic[0].title];
        } else if (trend && trend.length > 0) {
            newFilters[BLOG_TERM_TRENDS] = [trend[0].title];
        } else if (industry && industry.length > 0) {
            newFilters[BLOG_TERM_INDUSTRIES] = [industry[0].title];
        }

        setFilters(newFilters);
    }, [data]);

    useEffect(() => {
        const areFiltersEmpty = [BLOG_TERM_INDUSTRIES, BLOG_TERM_TOPICS, BLOG_TERM_TRENDS].every(
            (term) => filters[term].length === 0
        );
        if (!areFiltersEmpty) {
            fetchBlogs();
        }
    }, [filters, fetchBlogs]);

    // states for listing the categories for this blog
    const isCategoriesAvailable = topic?.length > 0 || trend?.length > 0 || industry?.length > 0;
    const categoriesObject = { topic: topic ?? [], trend: trend ?? [], industry: industry ?? [] };

    return (
        <>
            <section className="blog-single-entry pb-10 relative">
                <div className="container">
                    <SocialShare
                        pageTitle={title}
                        pageSlug={`${BLOG_PATHNAME}/${slug}`}
                        startElementSelector=".hero-banner-blog"
                        stopElementSelector=".blog-single-entry"
                        startElementPosition="bottom"
                        stopElementPosition="bottom"
                        additionalClasses="lg:mb-17.5"
                    />
                    <div className="row">
                        <div className="col-12">
                            <div className="lg:mx-25">
                                {data.body?.raw && (
                                    <div className={`${styles.richtextWrapper} ${blogStyles.blogRichtextWrapper}`}>
                                        <RichText data={data} />
                                    </div>
                                )}
                                {/* list of categories linking to blog listing with pre filters */}
                                {isCategoriesAvailable && (
                                    <div className="my-10 flex flex-wrap gap-2">
                                        <p className="font-semibold">{blogCategoriesHeading}</p>
                                        {Object.entries(categoriesObject).map(([category, values]) =>
                                            values.map((filterValue) => {
                                                const taxonomySlug = getTransformedId(filterValue.title);
                                                return (
                                                    <Link
                                                        to={`/${BLOG_PATHNAME}/${category}/${taxonomySlug}`}
                                                        key={filterValue.title}
                                                    >
                                                        {filterValue.title}
                                                    </Link>
                                                );
                                            })
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            {relatedPosts?.cardItems?.length > 0 && <CardImage data={relatedPosts} />}
        </>
    );
};

export default BlogSingleEntry;
